import { useEffect, useMemo, useState } from 'react';

import { Divider } from '@mui/material';

import { useQuery } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import APIs from '../../../api';
import profile from '../../../api/general/profile';
import { ApiResult } from '../../../api/types';
import ApprovalProgressDialog from '../../../common/approval/components/ApprovalProgressDialog';
import ApprovalProgressTableCell from '../../../common/approval/components/ApprovalProgressTableCell';
import ApproveRejectCell from '../../../common/approval/components/ApproveRejectCell';
import ApproveRejectDialog from '../../../common/approval/components/ApproveRejectDialog';
import Table from '../../../common/filterTable/components/Table';
import FilterContainer from '../../../common/filterTable/layout/FilterContainer';
import FilterSectionActionRow from '../../../common/filterTable/layout/FilterSectionActionRow';
import FilterTableLayoutContainer from '../../../common/filterTable/layout/FilterTableLayoutContainer';
import FilterTopActionRow from '../../../common/filterTable/layout/FilterTopActionRow';
import TableContainer from '../../../common/filterTable/layout/TableContainer';
import useNewFilterTablePage from '../../../common/page/hooks/useNewFilterTablePage';
import { ExportBtn, OpIconButton } from '../../../components/Button';
import { useDatePicker } from '../../../components/DatePicker';
import Permissions from '../../../constants/Permissions';
import { EnumTransactionStatus } from '../../../enum/EnumTransactionStatus';
import { useAlerting } from '../../../hooks';
import useTranslation, { Iprefix } from '../../../hooks/useTranslation';
import { useWeb3 } from '../../../hooks/useWeb3';
import { useAppSelector } from '../../../reducer/hooks';
import { selectProfile } from '../../../reducer/profileSlice';
import ApproveRejectContent from './ApproveRejectContent';
import FilterSection from './components/FilterSection';
import useGetListParams from './helpers/useGetListParams';
import useTableColumns from './helpers/useTableColumns';
import useTableRows from './helpers/useTableRows';
import { FilterFields, FilterFieldsKeys } from './types/FilterFields';
import { GetListRes } from './types/GetListRes';
import { TableRow } from './types/Table';

const initFields = {
  [FilterFieldsKeys.Chain]: '',
  [FilterFieldsKeys.AssetName]: [],
  [FilterFieldsKeys.BatchID]: '',
  [FilterFieldsKeys.OperationType]: '',
  [FilterFieldsKeys.Status]: '',
  [FilterFieldsKeys.MerchantIds]: [],
};

export const translatePrefix: Iprefix = 'transactionRequest';

const getTableDataApiFunc = APIs.TransactionRequest.getAll;
const exportTableApiFunc = APIs.TransactionRequest.getExport;

const exportBtnPermission = Permissions.TransactionRequest.Export;

const TransactionRequest = () => {
  const [isShowApproveRejectDialog, setIsShowApproveRejectDialog] = useState(false);
  const [isApprovalProgressDialogOpen, setIsApprovalProgressDialogOpen] = useState(false);
  const [isShowViewDialog, setIsShowViewDialog] = useState(false);
  const [signatures, setSignatures] = useState<string[]>([]);
  const [checkStatusRes, setCheckStatusRes] = useState<number | undefined>(undefined);

  const [searchParams] = useSearchParams();
  const acceptLevelParam = searchParams.get('acceptLevel');
  const [selectedRow, setSelectedRow] = useState<any>(undefined);

  const profile = useAppSelector(selectProfile);

  const DateObj = {
    CreatedDate: useDatePicker({ isTimezoneConvert: false }),
    LastModifiedDate: useDatePicker({ isTimezoneConvert: false }),
  };

  const { getListParams } = useGetListParams(DateObj.CreatedDate, DateObj.LastModifiedDate);

  const pageConfig = {
    initFields,
    DateObj,
    translatePrefix,
    getListParams,
    getTableDataApiFunc,
    exportTableApiFunc,
  };

  const {
    page,
    onPageChange,
    fields,
    setFields,
    list,
    onFilterSearch,
    onFilterReset,
    onExport,
    getIsHavePermission,
    isCollapse,
    toggleFilterCollapse,
    refreshTable,
  } = useNewFilterTablePage<FilterFields, ApiResult<GetListRes>>(pageConfig);

  useEffect(() => {
    onFilterSearch();
  }, [acceptLevelParam]);

  const renderOperationCell = (params: any) => {
    return (
      <>
        <ApproveRejectCell
          onClick={() => {
            setSelectedRow(params.row);
            setIsShowApproveRejectDialog(true);
          }}
          // isDisabled={!(Number(params.row.rawData.status) === EnumTransactionStatus.Pending)}
        />
      </>
    );
  };

  function renderApprovalProgressCell({ row }: { row: TableRow }) {
    return (
      <ApprovalProgressTableCell
        approvalsCount={row.rawData.approvalCompleted}
        approvalsRequired={row.rawData.approvalRequired}
        // applicationStatus={row.rawData.status}
        // approvedStatus={EnumTransactionStatus.Approved}
        onButtonClick={() => {
          setSelectedRow(row);
          setIsApprovalProgressDialogOpen(true);
        }}
      />
    );
  }

  const { rows, totalCounts, convertToTableRows } = useTableRows({ apiList: list });
  const columns = useTableColumns({ renderOperationCell, renderApprovalProgressCell });

  const isShowExportBtn = getIsHavePermission(exportBtnPermission);

  const handleExport = async () => {
    await onExport('TransactionRequest', totalCounts, convertToTableRows);
  };

  const allowedSignerAddresses = useMemo(() => {
    return JSON.parse(selectedRow?.rawData?.multiSigRawTxs || '[]')?.[0]?.multi_sig_raw_tx
      .signer_addresses;
  }, [selectedRow?.rawData?.multiSigRawTxs]);

  const web3 = useWeb3({
    allowedSignerAddresses: allowedSignerAddresses,
  });

  return (
    <>
      <FilterTableLayoutContainer>
        <ApproveRejectDialog
          isOpen={isShowApproveRejectDialog}
          showApproveHistory={false}
          closeDialog={() => {
            setIsShowApproveRejectDialog(false);
            setSignatures([]);
            setCheckStatusRes(undefined);
          }}
          refreshTable={refreshTable}
          selectedRow={selectedRow}
          getApproveHistory={APIs.TransactionRequest.getApprovalHistory}
          data={{
            operator: profile.email,
            createdBy: selectedRow?.createdBy || '',
            approvalsRequired: selectedRow?.rawData?.approvalRequired,
            approvalsCount: selectedRow?.rawData?.approvalCompleted,
            id: selectedRow?.rawData?.id,
            signaturesCollected: signatures.filter((sig) => sig && sig.length > 0).length,
            signatureRequired: JSON.parse(selectedRow?.rawData?.multiSigRawTxs || '[]').length,
            signatures,
            allowedSignerAddresses: allowedSignerAddresses,
          }}
          actions={{
            approve: {
              // apiFunc: APIs.TransactionRequest.approve,
              signApiCall: APIs.TransactionRequest.signApprove,
              permission: Permissions.TransactionRequest.Approve,
            },
            reject: {
              apiFunc: APIs.TransactionRequest.reject,
              permission: Permissions.TransactionRequest.Reject,
            },
          }}
          web3={web3}
          checkStatusRes={checkStatusRes}
        >
          <ApproveRejectContent
            selectedRow={selectedRow}
            checkStatusRes={checkStatusRes}
            setCheckStatusRes={setCheckStatusRes}
            signatures={signatures}
            setSignatures={setSignatures}
            web3={web3}
          />
        </ApproveRejectDialog>

        <ApprovalProgressDialog
          isOpen={isApprovalProgressDialogOpen}
          closeDialog={() => setIsApprovalProgressDialogOpen(false)}
          id={selectedRow?.rawData.id || ''}
          getApproveHistory={APIs.TransactionRequest.getApprovalHistory}
        />

        <FilterTopActionRow>
          <ExportBtn onExport={handleExport} isShow={isShowExportBtn} />
        </FilterTopActionRow>

        <Divider />

        <FilterContainer>
          <FilterSectionActionRow
            isCollapse={isCollapse}
            toggleFilterCollapse={toggleFilterCollapse}
            onSearch={onFilterSearch}
            onReset={() => {
              onFilterReset();
              DateObj.CreatedDate.clearDate();
              DateObj.LastModifiedDate.clearDate();
            }}
          />
          <FilterSection
            fields={fields}
            setFields={setFields}
            DateObj={DateObj}
            isCollapse={isCollapse}
          />
        </FilterContainer>

        <Divider />

        <TableContainer>
          <Table
            columns={columns}
            rows={rows}
            rowCount={totalCounts}
            page={page}
            onPageChange={onPageChange}
          />
        </TableContainer>
      </FilterTableLayoutContainer>
    </>
  );
};

export default TransactionRequest;
